import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Calendar, PhoneCall, Video, MapPin, Heart, Search } from "lucide-react";
import { Button } from "../components/Button";

const doctors = [
  { city: "New York", name: "Dr. John Doe", specialty: "Dermatologist", experience: "10 years", available: "Mon-Fri", rating: 4.8, mode: "In-person & Online", address: "123 Main St, NY", fee: "$100", image: "https://via.placeholder.com/100" },
  { city: "New York", name: "Dr. Sarah Lee", specialty: "Skin Specialist", experience: "8 years", available: "Tue-Sat", rating: 4.6, mode: "Online Only", address: "456 Park Ave, NY", fee: "$80", image: "https://via.placeholder.com/100" },
  { city: "Los Angeles", name: "Dr. Emily Clark", specialty: "Dermatologist", experience: "12 years", available: "Mon-Fri", rating: 4.9, mode: "In-person & Online", address: "789 Sunset Blvd, LA", fee: "$120", image: "https://via.placeholder.com/100" },
  { city: "Los Angeles", name: "Dr. Robert Smith", specialty: "Cosmetic Dermatologist", experience: "15 years", available: "Wed-Sun", rating: 5.0, mode: "Online Only", address: "101 Rodeo Dr, LA", fee: "$150", image: "https://via.placeholder.com/100" },
];

export default function Consultation() {
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [search, setSearch] = useState("");

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-100">
      {/* Sidebar for Doctor List */}
      <div className="w-full md:w-1/3 lg:w-1/4 bg-white shadow-md p-4 mt-16 overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Available Doctors</h2>
        <div className="relative mb-4">
          <Search className="absolute left-2 top-2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by city or specialty..."
            className="w-full pl-8 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {doctors.filter(doc => doc.city.toLowerCase().includes(search.toLowerCase()) || doc.specialty.toLowerCase().includes(search.toLowerCase())).map((doc, index) => (
          <motion.div
            key={index}
            className={`p-3 border-b cursor-pointer flex items-center gap-3 hover:bg-gray-200 transition-colors duration-200 ${
              selectedDoctor?.name === doc.name ? "bg-gray-300" : ""
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedDoctor(doc)}
          >
            <img src={doc.image} alt={doc.name} className="w-10 h-10 rounded-full" />
            <div>
              <h3 className="text-lg font-semibold">{doc.name}</h3>
              <p className="text-gray-600">{doc.specialty}</p>
              <p className="text-gray-500">{doc.city}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Content */}
      <div className="w-full md:w-2/3 lg:w-3/4 p-6 flex flex-col items-center justify-center relative">
        {!selectedDoctor ? (
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Importance of Skin Care</h2>
            <p className="text-gray-600 max-w-xl mb-6">
              Taking care of your skin is essential for overall health and confidence. Regular check-ups with dermatologists help prevent serious skin conditions.
            </p>
            <motion.div
              className="absolute inset-0 w-full h-full bg-gradient-to-r from-purple-300 via-pink-300 to-blue-400 opacity-30 animate-pulse"
            ></motion.div>
            <motion.div
              className="w-40 h-40 md:w-64 md:h-64 bg-gradient-to-r from-purple-400 to-blue-500 rounded-full flex items-center justify-center shadow-lg z-10"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ repeat: Infinity, duration: 3 }}
            >
              <Heart className="text-white h-12 w-12 md:h-16 md:w-16 animate-pulse" />
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg w-full max-w-md text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <img src={selectedDoctor.image} alt={selectedDoctor.name} className="w-20 h-20 md:w-24 md:h-24 rounded-full mx-auto mb-4" />
            <h3 className="text-xl md:text-2xl font-semibold mb-1">{selectedDoctor.name}</h3>
            <p className="text-gray-600">{selectedDoctor.specialty}</p>
            <p className="text-gray-500">{selectedDoctor.experience} experience</p>
            <p className="text-gray-500 flex items-center justify-center gap-1">
              <Star className="h-5 w-5 text-yellow-500" /> {selectedDoctor.rating}
            </p>
            <p className="text-gray-500 mt-2 flex items-center justify-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" /> Available: {selectedDoctor.available}
            </p>
            <p className="text-gray-500 mt-2 flex items-center justify-center gap-2">
              <MapPin className="h-5 w-5 text-red-500" /> {selectedDoctor.address}
            </p>
            <p className="text-gray-500 mt-2">Consultation Fee: <strong>{selectedDoctor.fee}</strong></p>
            <div className="flex flex-col gap-2 mt-4">
              <Button className="w-full">Book Appointment</Button>
              <Button className="w-full bg-gray-300 text-black hover:bg-gray-400" onClick={() => setSelectedDoctor(null)}>
                Back to List
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}